// src/components/product/ProductDetailsSection.js
import React from "react";
import { Tag, ChevronDown } from "lucide-react";
import { ProductStateDropdown } from "./ProductStateDropdown";
import { Input, Textarea } from "../ui";

export function ProductDetailsSection({
  form,
  categoryOptions,
  cropSuggestions,
  productStateOptions,
  showCategoryDropdown,
  showCropDropdown,
  storageCondition,
  onInputChange,
  onCategorySelect,
  setShowCategoryDropdown,
  setShowCropDropdown,
  setStorageCondition,
  categoryDropdownRef,
  labels = {}
}) {
  const shouldShowTitle = labels.sectionTitle !== null;

  return (
    <div className="bg-white dark:bg-neutral-800/80 rounded-xl p-4 sm:p-6 shadow-md dark:shadow-lg border border-gray-200 dark:border-neutral-700/50 space-y-4">
      {shouldShowTitle && (
        <div className="flex items-center gap-2 mb-2">
          <Tag className="w-5 h-5 text-green-600" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {labels.sectionTitle ?? "Product Details"}
          </h2>
        </div>
      )}

      {/* Category (first) */}
      <div className="md:grid md:grid-cols-12 md:items-center md:gap-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 md:mb-0 md:col-span-3">
          {labels.categoryLabel || "Category"}
        </label>
        <div className="relative md:col-span-9" ref={categoryDropdownRef}>
          <button
            type="button"
            onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-neutral-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-green-600 dark:focus:ring-green-500 flex items-center justify-between text-left"
          >
            <span>{form.category ? (categoryOptions.find((c) => c.value === form.category)?.label || form.category) : (labels.categoryPlaceholder || "Select a category...")}</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${showCategoryDropdown ? "rotate-180" : ""}`} />
          </button>
          {/* Category Dropdown - Appears Above */}
          {showCategoryDropdown && (
            <div className="absolute bottom-full left-0 right-0 mb-1 max-h-56 overflow-y-auto bg-white dark:bg-neutral-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-20">
              {categoryOptions.map((cat) => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => {
                    onCategorySelect(cat.value);
                    setShowCategoryDropdown(false);
                    setShowCropDropdown(false);
                  }}
                  className={`w-full text-left px-3 py-2.5 text-sm border-b border-gray-200 dark:border-gray-700 last:border-b-0 transition ${
                    form.category === cat.value
                      ? "bg-green-100 dark:bg-green-900/30 text-green-900 dark:text-green-100 font-medium"
                      : "hover:bg-gray-50 dark:hover:bg-neutral-700 text-gray-900 dark:text-gray-100"
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Product Name */}
      <div className="md:grid md:grid-cols-12 md:items-center md:gap-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 md:mb-0 md:col-span-3">
          {labels.productNameLabel || "Product Name"}
        </label>
        <div className="relative md:col-span-9">
          <Input
            type="text"
            name="title"
            value={form.title}
            onChange={(e) => {
              onInputChange(e);
              setShowCropDropdown(true);
            }}
            onFocus={() => cropSuggestions.length > 0 && setShowCropDropdown(true)}
            required
            placeholder={form.category ? (labels.productNamePlaceholder || "Search or enter crop name...") : (labels.selectCategoryFirst || "Select category first")}
            disabled={!form.category}
          />
          {showCropDropdown && form.category && cropSuggestions.length > 0 && (
            <div className="absolute bottom-full left-0 right-0 mb-1 max-h-48 overflow-y-auto bg-white dark:bg-neutral-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-20">
              {cropSuggestions
                .filter((crop) => crop.toLowerCase().includes(form.title.toLowerCase()))
                .slice(0, 8)
                .map((crop) => (
                  <button
                    key={crop}
                    type="button"
                    onClick={() => {
                      onInputChange({ target: { name: "title", value: crop } });
                      setShowCropDropdown(false);
                    }}
                    className="w-full text-left px-3 py-2.5 hover:bg-green-50 dark:hover:bg-green-900/20 text-sm text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 last:border-b-0"
                  >
                    {crop}
                  </button>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Product State & Storage Condition */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <div className="md:grid md:grid-cols-12 md:items-center md:gap-3 md:col-span-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 md:mb-0 md:col-span-4">
            {labels.productStateLabel || "Product State"}
          </label>
          <div className="md:col-span-8">
            <ProductStateDropdown
              productStateOptions={productStateOptions}
              value={form.productState}
              onChange={onInputChange}
              disabled={!form.category}
              category={form.category}
              labels={labels}
            />
          </div>
        </div>

        <div className="md:grid md:grid-cols-12 md:items-center md:gap-3 md:col-span-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 md:mb-0 md:col-span-4">
            {labels.storageConditionLabel || "Storage Condition"}
          </label>
          <div className="md:col-span-8">
            <select
              value={storageCondition}
              onChange={(e) => setStorageCondition(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-neutral-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-green-600 dark:focus:ring-green-500"
            >
              <option value="ambient">{labels.storageAmbient || "Ambient"}</option>
              <option value="refrigerated">{labels.storageRefrigerated || "Refrigerated"}</option>
              <option value="frozen">{labels.storageFrozen || "Frozen"}</option>
              <option value="controlled_atmosphere">{labels.storageControlledAtmosphere || "Controlled Atmosphere"}</option>
              <option value="vacuum_sealed">{labels.storageVacuumSealed || "Vacuum Sealed"}</option>
              <option value="modified_atmosphere">{labels.storageModifiedAtmosphere || "Modified Atmosphere"}</option>
              <option value="dry_storage">{labels.storageDryStorage || "Dry Storage"}</option>
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {labels.suggestedStorage || "Suggested"}: <span className="font-semibold text-green-600 dark:text-green-400">{storageCondition}</span>
            </p>
          </div>
        </div>
      </div>

      <div className="md:grid md:grid-cols-12 md:items-start md:gap-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 md:mb-0 md:pt-2 md:col-span-3">
          {labels.descriptionLabel || "Description"}
        </label>
        <div className="md:col-span-9">
          <Textarea
            name="desc"
            value={form.desc}
            onChange={onInputChange}
            placeholder={labels.descriptionPlaceholder || "Add details about the product..."}
            rows={4}
          />
        </div>
      </div>
    </div>
  );
}